import type { ReactElement } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FaPrint, FaTimes } from "react-icons/fa";
import { printPage } from "../../../utils/printPage";
import { cellText, exportColumns } from "../excelExport";
import type { Catalog, ReportResult, ReportSpec } from "../types";
import { ghostButton, primaryButton } from "./styles";

type Props = {
  catalog: Catalog;
  definition: ReportSpec;
  result: ReportResult;
  title: string;
  subtitle: string;
  onClose: () => void;
};

/**
 * Print preview of all rows. Printing is started from the button here, directly in the click (Safari needs a
 * user action); when printing, everything else on the page is hidden.
 */
export default function PrintView({ catalog, definition, result, title, subtitle, onClose }: Props): ReactElement {
  const { t } = useTranslation();
  const columns = exportColumns(t, catalog, definition, result);
  const printedOn = new Date().toLocaleDateString("nl-NL");

  return createPortal(
    <div className="report-builder-print fixed inset-0 z-[100] overflow-auto bg-white text-slate-900">
      <style>{`
        @media print {
          body > *:not(.report-builder-print) { display: none !important; }
          .report-builder-print { position: static !important; overflow: visible !important; }
          .report-builder-print-controls { display: none !important; }
          .report-builder-print table { font-size: 10px; }
          .report-builder-print tr { break-inside: avoid; }
          @page { size: landscape; margin: 12mm; }
        }
      `}</style>
      <div className="report-builder-print-controls sticky top-0 flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-6 py-3">
        <span className="text-sm font-bold">{t("reports.builder.print_preview", { defaultValue: "Print preview" })}</span>
        <div className="flex gap-2">
          <button type="button" className={ghostButton} onClick={onClose}>
            <FaTimes aria-hidden /> {t("common.close", { defaultValue: "Close" })}
          </button>
          <button type="button" className={primaryButton} onClick={() => printPage()}>
            <FaPrint aria-hidden /> {t("reports.builder.print_now", { defaultValue: "Print" })}
          </button>
        </div>
      </div>
      <div className="px-6 py-4">
        <h1 className="text-xl font-bold">{title}</h1>
        <p className="mb-3 text-xs text-slate-600">
          {subtitle} · {t("reports.builder.row_count", { count: result.total, defaultValue: "{{count}} rows" })}
          {" · "}{t("reports.builder.printed_on", { date: printedOn, defaultValue: "Printed on {{date}}" })}
        </p>
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr>
              {columns.map(({ column, header }) => (
                <th key={column.key} className="border-b-2 border-slate-800 px-2 py-1 font-bold">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={index} className="align-top">
                {columns.map(({ column }) => (
                  <td key={column.key} className="border-b border-slate-200 px-2 py-1">
                    {column.display === "LIST"
                      ? cellText(t, catalog, definition, column, row).split("\n").map((line, lineIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={lineIndex} className="whitespace-nowrap">{line}</div>
                      ))
                      : cellText(t, catalog, definition, column, row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>,
    document.body,
  );
}
